import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { alertsAPI } from '../../services/api';
import {
  LayoutDashboard,
  Coins,
  Network,
  Play,
  Bell,
  LogOut,
  X,
  Check,
} from 'lucide-react';

export function Layout() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [showNotifications, setShowNotifications] = useState(false);

  // 읽지 않은 알림 조회
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => alertsAPI.getNotifications(10),
    refetchInterval: 30000, // 30초마다 갱신
  });

  const markAsReadMutation = useMutation({
    mutationFn: (historyId: string) => alertsAPI.markHistoryAsRead(historyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['alert-history'] });
    },
  });

  const unreadCount = notifications?.data?.unread_count || 0;
  const notificationList = notifications?.data?.notifications || [];

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Assets', href: '/assets', icon: Coins },
    { name: 'Protocol Graph', href: '/graph', icon: Network },
    { name: 'Model Playground', href: '/playground', icon: Play },
    { name: 'Alerts', href: '/alerts', icon: Bell },
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-primary-600">COINFUNDMENTAL</h1>
              <span className="px-2 py-0.5 text-xs font-semibold text-orange-600 bg-orange-100 rounded-full">
                BETA
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.email || 'User'}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              {/* 알림 벨 */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {/* 알림 드롭다운 */}
                {showNotifications && (
                  <div className="absolute bottom-full right-0 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">알림</h3>
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {notificationList.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {notificationList.map((notification: any) => (
                            <div
                              key={notification.id}
                              className="p-4 hover:bg-gray-50 cursor-pointer"
                              onClick={() => {
                                markAsReadMutation.mutate(notification.id);
                                window.location.href = `/alerts`;
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm text-gray-900">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(notification.triggered_at).toLocaleString('ko-KR', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                  {notification.channels && notification.channels.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {notification.channels.join(', ')}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsReadMutation.mutate(notification.id);
                                  }}
                                  className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                                  title="읽음 처리"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">읽지 않은 알림이 없습니다.</p>
                        </div>
                      )}
                    </div>
                    {notificationList.length > 0 && (
                      <div className="p-3 border-t border-gray-200">
                        <Link
                          to="/alerts"
                          onClick={() => setShowNotifications(false)}
                          className="block text-center text-sm text-primary-600 hover:text-primary-700"
                        >
                          모든 알림 보기
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-64">
        <main className="p-8">
          <Outlet />
        </main>
        
        {/* Footer */}
        <footer className="border-t border-gray-200 bg-white py-4 px-8">
          <div className="flex justify-center gap-4 text-xs text-gray-500">
            <a href="/privacy" className="hover:text-primary-600">
              개인정보처리방침
            </a>
            <span className="text-gray-300">|</span>
            <a href="/service" className="hover:text-primary-600">
              서비스 이용약관
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

